import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../../../context/ThemeContext";
import { Wallet, CheckCircle, Shield, ArrowLeft, Clock } from "lucide-react";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { ReactComponent as Mastercard } from '../../../assets/mastercard (2).svg';
import { ReactComponent as Visa } from '../../../assets/visa.svg';
import { ReactComponent as Jazzcash } from '../../../assets/JazzCash (2).svg';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkTheme } = useTheme();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("mastercard");
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({});
  const [timeLeft, setTimeLeft] = useState(900); // 15 minutes in seconds

  // Get state from navigation
  const { 
    selectedPlan = "Premium", 
    billingFrequency = "monthly", 
    price = "$49.99",
    isAnnual = false,
    features = ["Unlimited AI Chats", "Advanced Features", "Priority Support"]
  } = location.state || {};

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const paymentMethods = [
    {
      id: "mastercard",
      name: "MasterCard",
      icon: <Mastercard className="w-6 h-6" />,
      color: "#FF5F00",
      inputs: [
        { name: "cardNumber", label: "Card Number", placeholder: "1234 5678 9012 3456", type: "text" },
        { name: "expiryDate", label: "Expiry Date", placeholder: "MM/YY", type: "text" },
        { name: "cvv", label: "Security Code (CVV)", placeholder: "123", type: "text" },
      ],
    },
    {
      id: "visa",
      name: "Visa Card",
      icon: <Visa className="w-6 h-6" />,
      color: "#1A1F71",
      inputs: [
        { name: "cardNumber", label: "Card Number", placeholder: "1234 5678 9012 3456", type: "text" },
        { name: "expiryDate", label: "Expiry Date", placeholder: "MM/YY", type: "text" },
        { name: "cvv", label: "Security Code (CVV)", placeholder: "123", type: "text" },
      ],
    },
    {
      id: "jazzcash",
      name: "Jazz Cash",
      icon: <Jazzcash  className="w-6 h-6" />,
      color: "#F0131E",
      inputs: [
        { name: "phoneNumber", label: "Phone Number", placeholder: "0300 1234567", type: "tel" },
        { name: "pin", label: "PIN", placeholder: "1234", type: "password" },
      ],
    },
  ];

  const handlePayment = () => {
    if (!selectedPaymentMethod) {
      toast.error("Please select a payment method.");
      return;
    }

    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      toast.success("Payment successful!");
      navigate("/chat");
    }, 2000);
  };

  const selectedMethod = paymentMethods.find(method => method.id === selectedPaymentMethod);

  return (
    <div className={`min-h-screen ${isDarkTheme ? "bg-gradient-to-br from-gray-900 to-black text-gray-100" : "bg-gradient-to-br from-gray-50 to-white text-gray-800"}`}>
      {/* Floating Timer */}
      <div className={`fixed top-4 right-4 ${isDarkTheme ? "bg-gray-800" : "bg-white"} p-3 rounded-full shadow-lg flex items-center space-x-2 z-10`}>
        <Clock className="w-5 h-5 text-amber-500" />
        <span className="font-mono font-medium">{formatTime(timeLeft)}</span>
      </div>

      {/* Header */}
      <div className={`px-6 py-3 border-b ${isDarkTheme ? "border-gray-700" : "border-gray-200"} flex items-center backdrop-blur-sm bg-opacity-80 ${isDarkTheme ? "bg-gray-900" : "bg-white"} sticky top-0 z-10`}>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="flex items-center"
        >
          <CheckCircle className="w-6 h-6 text-amber-500 mr-3" />
          <h1 className="text-2xl font-bold">Secure Checkout</h1>
        </motion.div>
        <button
          onClick={() => navigate("/upgrade")}
          className={`ml-auto px-4 py-2 rounded-full flex items-center ${
            isDarkTheme ? "bg-gray-800 hover:bg-gray-700 text-gray-300" : "bg-gray-200 hover:bg-gray-300 text-gray-700"
          } transition duration-200`}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Plan Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className={`sticky top-24 ${isDarkTheme ? "bg-gray-800" : "bg-white"} rounded-2xl overflow-hidden shadow-lg`}
            >
              <div className={`p-1 ${isDarkTheme ? "bg-gradient-to-r from-amber-500 to-orange-500" : "bg-gradient-to-r from-amber-400 to-orange-400"}`}>
                <div className={`p-6 rounded-xl ${isDarkTheme ? "bg-gray-800" : "bg-white"}`}>
                  <h2 className="text-2xl font-bold mb-4 flex items-center">
                    <span className={`inline-block mr-2 p-1 rounded-full ${isDarkTheme ? "bg-gray-700" : "bg-amber-100"}`}>
                      <CheckCircle className="w-5 h-5 text-amber-500" />
                    </span>
                    {selectedPlan} Plan
                  </h2>
                  
                  <div className="flex justify-between items-baseline mb-6">
                    <span className={`text-3xl font-bold text-amber-500`}>{price}</span>
                    <span className={`text-sm ${isDarkTheme ? "text-gray-400" : "text-gray-600"}`}>
                      per {isAnnual ? "year" : "month"}
                    </span>
                  </div>
                  
                  <div className={`p-4 rounded-xl mb-6 ${isDarkTheme ? "bg-gray-700 bg-opacity-50" : "bg-gray-100"}`}>
                    <p className="text-sm font-medium mb-2">Plan includes:</p>
                    <ul className="space-y-2">
                      {features.map((feature, index) => (
                        <li key={index} className="flex items-center">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className={`p-4 rounded-xl ${isDarkTheme ? "bg-gray-700 bg-opacity-50" : "bg-gray-100"}`}>
                    <div className="flex items-center mb-2">
                      <Shield className="w-4 h-4 text-amber-500 mr-2" />
                      <span className="text-sm font-medium">Secure Payment</span>
                    </div>
                    <p className={`text-xs ${isDarkTheme ? "text-gray-400" : "text-gray-600"}`}>
                      All transactions are secured and encrypted
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* Right Column - Payment Methods */}
          <div className="lg:col-span-2">
            {/* Payment Methods */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mb-8"
            >
              <h2 className="text-xl font-semibold mb-4">Select Payment Method</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {paymentMethods.map((method) => (
                  <div
                    key={method.id}
                    className={`p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                      selectedPaymentMethod === method.id
                        ? "border-2 border-amber-500 shadow-md transform scale-105"
                        : isDarkTheme
                        ? "bg-gray-800 hover:bg-gray-700 border border-gray-700"
                        : "bg-white hover:bg-gray-50 border border-gray-100 hover:border-gray-200"
                    }`}
                    onClick={() => setSelectedPaymentMethod(method.id)}
                  >
                    <div className="flex flex-col items-center justify-center">
                      <div 
                        className={`flex items-center justify-center w-10 h-10 p-3 rounded-full mb-2`}
                        style={{
                          backgroundColor: selectedPaymentMethod === method.id
                            ? method.id === "mastercard"
                              ? "#000000"
                              : method.id === "jazzcash"
                                ? "#FDE8E8" 
                                : method.color
                            : isDarkTheme ? '#2A2A2A' : '#F0F0F0',
                          opacity: selectedPaymentMethod === method.id ? 1 : 0.7
                        }}
                      >
                        {method.icon}
                      </div>
                      <span className="text-sm font-medium text-center">{method.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Payment Details */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mb-8"
            >
              <h2 className="text-xl font-semibold mb-4">Payment Details</h2>
              <div className={`p-6 rounded-xl ${isDarkTheme ? "bg-gray-800 border border-gray-700" : "bg-white shadow-md"} space-y-4`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedMethod.inputs.map((input, index) => (
                    <div key={index} className="relative">
                      <label className={`block text-sm font-medium mb-1 ${isDarkTheme ? "text-gray-300" : "text-gray-700"}`}>
                        {input.label}
                      </label>
                      <input
                        type={input.type}
                        name={input.name}
                        placeholder={input.placeholder}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-lg border ${
                          isDarkTheme
                            ? "bg-gray-700 border-gray-600 text-gray-100 focus:border-amber-500"
                            : "bg-gray-50 border-gray-200 text-gray-800 focus:border-amber-500"
                        } focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-opacity-50 transition-all duration-200`}
                      />
                    </div>
                  ))}
                </div>
                
                {/* Security message */}
                <div className={`mt-4 p-3 rounded-lg flex items-center ${isDarkTheme ? "bg-gray-700 bg-opacity-50" : "bg-amber-50"}`}>
                  <Shield className={`w-5 h-5 ${isDarkTheme ? "text-amber-400" : "text-amber-500"} mr-2 mt-0.5`} />
                  <p className={`text-xs ${isDarkTheme ? "text-gray-300" : "text-amber-800"}`}>
                    Your payment information is secure. We use industry-standard encryption to protect your data.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Payment Button */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex justify-center"
            >
              <button
                onClick={handlePayment}
                disabled={isProcessing}
                className={`w-full md:w-2/3 py-4 rounded-xl text-lg font-semibold transition-all duration-300 flex items-center justify-center ${
                  isProcessing
                    ? "bg-gray-500 text-gray-300 cursor-not-allowed"
                    : "bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-xl hover:from-orange-500 hover:to-amber-500"
                }`}
              >
                {isProcessing ? (
                  <>
                    <svg className="animate-spin mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  "Complete Purchase"
                )}
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;